import React from 'react';
import styled from 'styled-components';
import type { Fund, Portfolio } from 'venn-api';
import { Icon, ShimmerBlock } from 'venn-ui-kit';
import {
  findParent,
  MANAGE_DATA_SECTION,
  navigateToManageDataPage,
  normalizePortfolio,
  recursiveUpdateAllocations,
  updateNode,
} from 'venn-utils';
import type { DropPosition } from './AllocationTreeItem';
import AllocationTreeItem from './AllocationTreeItem';
import { TreeItemUpdateType } from './AllocationUtils';
import PrintContainerDimensions from '../print/PrintContainerDimensions';
import DragOverlay from './drag-and-drop/DragOverlay';
import { cloneDeep } from 'lodash';
import type { DeprecatedIncorrectlyDefinedItemPercentageProps } from './ItemAllocation';
import { AllocationPanelContext, type AllocationPanelContextValue } from './AllocationPanelContext';
import type { ProFormaNode } from './types';
import { hasData } from './allocation-panel-utils';
import type { History } from 'history';

export interface AllocationTreeProps extends DeprecatedIncorrectlyDefinedItemPercentageProps {
  portfolio: Portfolio;
  selectedStrategyId: number;
  compareLoading: boolean;
  allUpdatedFunds?: Map<string, Fund>;
  allOriginalNodes: Map<number, Portfolio>;
  allCompareNodes: Map<number, Portfolio>;
  allGhostChildren: Map<number, Portfolio[]>;
  hideCompareValue: boolean;
  isTradesView?: boolean;
  onUpdatePortfolio: (updatedPortfolio: Portfolio, updateType: TreeItemUpdateType) => void;
  onSelectStrategy?: (selectedStrategy: Portfolio) => void;
  onDragFinished?: () => void;
  setIsDragging?: (isDragging: boolean) => void;
  updateFund?: (fundId: string) => Promise<Fund | undefined>;
  hasAccessToCompare?: boolean;
  hideComparisonColumn: boolean;
  history: History;
}

type AllocationTreePropsInner = AllocationTreeProps & {
  context: AllocationPanelContextValue;
};

interface AllocationTreeState {
  draggedNode?: Portfolio;
  initialX?: number;
  initialY?: number;
}

class AllocationTree extends React.PureComponent<AllocationTreePropsInner, AllocationTreeState> {
  dragListener?: () => void = undefined;

  state: AllocationTreeState = {
    draggedNode: undefined,
    initialX: undefined,
    initialY: undefined,
  };

  componentWillUnmount() {
    if (this.dragListener) {
      window.removeEventListener('mouseup', this.dragListener);
    }
  }

  setDraggedNode = (draggedNode?: Portfolio) => {
    this.props.setIsDragging?.(!!draggedNode);

    if (!draggedNode) {
      this.setState({
        draggedNode: undefined,
        initialX: undefined,
        initialY: undefined,
      });
      return;
    }

    this.setState({ draggedNode });
  };

  onDrag = (event: React.MouseEvent<HTMLElement>, strategy: Portfolio) => {
    const { clientX, clientY } = event;
    this.setDraggedNode(strategy);
    this.setState({
      draggedNode: strategy,
      initialX: clientX,
      initialY: clientY,
    });

    this.dragListener = () => {
      if (this.dragListener) {
        window.removeEventListener('mouseup', this.dragListener);
      }
      this.props.onDragFinished?.();
      this.dragListener = undefined;
      this.setDraggedNode(undefined);
    };
    window.addEventListener('mouseup', this.dragListener);
  };

  onDrop = (dropOnNode: Portfolio, positionedAs: DropPosition) => {
    const { draggedNode } = this.state;
    if (!draggedNode) {
      return;
    }

    const { portfolio } = this.props;
    let updatedPortfolio = cloneDeep(portfolio);

    // 1. Remove the node from its old parent
    const oldParent = findParent(portfolio, draggedNode);
    const oldParentId = oldParent ? oldParent.id : undefined;
    updatedPortfolio = updateNode(updatedPortfolio, oldParentId, (node: Portfolio) => {
      const removeAtIdx = node.children.findIndex((child: Portfolio) => child.id === draggedNode?.id);
      const updatedChildren = [...node.children];
      updatedChildren.splice(removeAtIdx, 1);
      return {
        ...node,
        children: updatedChildren,
      };
    });

    // 2. Add the node under its new parent
    const newParent =
      positionedAs === 'FIRST_CHILD' || positionedAs === 'LAST_CHILD' ? dropOnNode : findParent(portfolio, dropOnNode);
    const newParentId = newParent ? newParent.id : undefined;
    updatedPortfolio = updateNode(updatedPortfolio, newParentId, (node: Portfolio) => {
      const addAtIdx =
        positionedAs === 'FIRST_CHILD'
          ? 0
          : positionedAs === 'LAST_CHILD'
            ? node.children.length
            : node.children.findIndex((child: Portfolio) => child.id === dropOnNode.id) + 1;
      const updatedChildren = [...node.children];
      updatedChildren.splice(addAtIdx, 0, draggedNode);
      return {
        ...node,
        children: updatedChildren,
      };
    });

    // 3. Update portfolio allocations
    updatedPortfolio = recursiveUpdateAllocations(updatedPortfolio);

    // 4. Normalize portfolio (remove duplicate funds from under the same strategy)
    updatedPortfolio = normalizePortfolio(updatedPortfolio);

    this.setDraggedNode(undefined);
    this.props.onUpdatePortfolio(
      updatedPortfolio,
      oldParentId !== newParentId ? TreeItemUpdateType.DRAG_N_DROP : TreeItemUpdateType.DRAG_N_DROP_REORDER_ONLY,
    );
  };

  render() {
    const {
      portfolio,
      selectedStrategyId,
      compareLoading,
      hideCompareValue,
      allUpdatedFunds,
      allOriginalNodes,
      allCompareNodes,
      allGhostChildren,
      onUpdatePortfolio,
      onSelectStrategy,
      isPercentageMode,
      baseAllocation,
      isTradesView,
      orignalBaseAllocation,
      updateFund,
      hideComparisonColumn,
      history,
      context,
    } = this.props;

    const { draggedNode, initialX, initialY } = this.state;

    const onSelectStrategyCallback =
      onSelectStrategy === undefined
        ? undefined
        : (selected: Portfolio | undefined) => onSelectStrategy(selected ?? portfolio);
    const compareStrategy = compareLoading
      ? undefined
      : allCompareNodes.has(portfolio.id)
        ? allCompareNodes.get(portfolio.id)
        : undefined;
    const compareStrategyAllocation = compareStrategy?.allocation;
    return context.allocationPanelType === 'historical read-only' &&
      context.status === 'error' &&
      context.errorType === 'insufficient overlap with returns' ? (
      <div className="flex flex-col items-center justify-start h-full text-center p-4">
        <div className="mb-8">
          <Icon prefix="fat" type="circle-x" className="text-[100px] text-venn-grey-30" />
        </div>
        <div>
          Unable to run analysis. There are insufficient returns available to analyze. Please review in{' '}
          <StyledButton
            onClick={() =>
              navigateToManageDataPage(
                history,
                { portfolioId: portfolio.id },
                'Studio',
                true,
                MANAGE_DATA_SECTION.ANALYSIS_RANGE,
              )
            }
          >
            Manage Data
          </StyledButton>
          .
        </div>
      </div>
    ) : context.allocationPanelType === 'historical read-only' && !hasData(context) ? (
      <ShimmerBlock className="[&&]:h-full [&&]:w-full [&&]:mb-0 flex-1" />
    ) : (
      <Tree>
        {draggedNode && <DragOverlay initialX={initialX ?? 0} initialY={initialY ?? 0} portfolioNode={draggedNode} />}
        <PrintContainerDimensions>
          {({ width }) => (
            <AllocationTreeItem
              totalWidth={width}
              strategy={
                context.allocationPanelType === 'historical read-only' ? context.portfolio : (portfolio as ProFormaNode)
              }
              selectedStrategyId={selectedStrategyId}
              onSelectStrategy={onSelectStrategyCallback}
              compareStrategy={compareStrategy}
              compareLoading={compareLoading}
              allOriginalNodes={allOriginalNodes}
              allCompareNodes={allCompareNodes}
              allGhostChildren={allGhostChildren}
              allUpdatedFunds={allUpdatedFunds}
              hideCompareValue={hideCompareValue}
              isRoot
              onStrategyUpdate={onUpdatePortfolio}
              draggedNode={draggedNode}
              onDrag={this.onDrag}
              onDrop={this.onDrop}
              isPercentageMode={isPercentageMode}
              baseAllocation={baseAllocation}
              orignalBaseAllocation={orignalBaseAllocation}
              secondaryTotal={compareStrategyAllocation}
              isTradesView={isTradesView}
              updateFund={updateFund}
              hasAccessToCompare={this.props.hasAccessToCompare}
              hideComparisonColumn={hideComparisonColumn}
              rootName={portfolio.name}
            />
          )}
        </PrintContainerDimensions>
      </Tree>
    );
  }
}

const AllocationTreeOuter = (props: AllocationTreeProps) => (
  <AllocationPanelContext.Consumer>
    {(context: AllocationPanelContextValue) => <AllocationTree {...props} context={context} />}
  </AllocationPanelContext.Consumer>
);
export default AllocationTreeOuter;

const Tree = styled.div`
  margin-top: 8px;
  padding-bottom: 60px;
  position: relative;
`;

export const StyledButton = styled.button`
  border: none;
  font-weight: bold;
  color: GetColor.Primary.Dark;
  font-size: inherit;

  &:hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }
`;
